import React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
    CardContent,
    CardHeader,
    Grid,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { at, filter, groupBy, keyBy, sortBy } from 'lodash/fp';
import { Category, CategoryProductCount } from '../../../../server-types';
import { Card } from '../../../../ui-kit';
import { mapIds } from '../../../../utils';
import { ChildCategory } from './ChildCategory';
import CategoryTitle from './CategoryTitle';

export interface CategoriesListViewProps {
    categories: Array<Category>;
    categoriesProductCount: Array<CategoryProductCount>;
}

const CategoryListView = ({ categories, categoriesProductCount }: CategoriesListViewProps) => {
    const { url } = useRouteMatch();
    const history = useHistory();

    const navigateTo = (id: string) => history.push(`${url}/${id}`);
    const productCountMap = keyBy('categoryId', categoriesProductCount);
    const topLevelCategories = filter<Category>(({ category }) => !category, categories);
    const sortedTopLevelCategories = sortBy(['ordering', 'name', 'asc'], topLevelCategories);
    const groupedByParent = groupBy('category.id', categories);
    const { t } = useTranslation();
    const productsLocalized = t('Products');

    return (
        <Grid container spacing={8}>
            {sortedTopLevelCategories.map(({ id, name, color }) => {
                    const childCategories = groupedByParent[id] || [];
                    const sortedChildCategories = sortBy(['name', 'asc'], childCategories);
                    const parentProductCount = productCountMap[id].productCount;
                    let totalProductCount = parentProductCount;

                    if(sortedChildCategories.length > 0) {
                        const childCategoryIds = mapIds(sortedChildCategories);
                        const childProductCount = at(childCategoryIds, productCountMap);
                        const count = childProductCount.reduce((acc, curr) => curr.productCount + acc, 0);
                        totalProductCount += count;
                    }
                    const onClick = () => navigateTo(id);
                    const title = (
                        <CategoryTitle
                            name={name}
                            productCount={totalProductCount}
                            color={color}
                            onClick={onClick}
                        />
                    );
                    return (
                        <Grid item xs={12} key={id}>
                            <Card elevation={1}>
                                <CardHeader
                                    title={title}
                                    disableTypography
                                />
                                <CardContent>
                                    {parentProductCount > 0
                                        ? (
                                            <ChildCategory
                                                key={id}
                                                categoryId={id}
                                                categoryName={productsLocalized}
                                                productCount={parentProductCount}
                                            />
                                        )
                                        : null}
                                    {sortedChildCategories.map(({ id: childId, name: childName }) => {
                                        const { productCount } = productCountMap[childId];

                                        return (
                                            <ChildCategory
                                                key={childId}
                                                parentColor={color}
                                                categoryName={childName}
                                                productCount={productCount}
                                                categoryId={childId}
                                            />
                                        );
                                    })}
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
        </Grid>
    );
};

export default CategoryListView;
